import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Edit2,
    Trash2,
    Eye,
    EyeOff,
    Calendar,
    Image,
    Link as LinkIcon,
    AlertCircle,
    CheckCircle,
    X,
    Search,
    Filter,
    Megaphone,
    Upload
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
    getAllAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    toggleAnnouncementStatus,
    getAnnouncementImageUrl
} from '../../services/announcement.service';

const CATEGORIES = [
    { value: 'mental-health', label: 'Mental Health' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'awareness', label: 'Awareness' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'support-group', label: 'Support Group' },
    { value: 'other', label: 'Other' }
];

const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
];

const AnnouncementManagement = () => {
    const { user } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        imageUrl: '',
        imageFile: null,
        imageType: 'none',
        link: '',
        category: 'other',
        priority: 'medium'
    });
    const [formLoading, setFormLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // Fetch announcements
    const fetchAnnouncements = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterCategory) params.category = filterCategory;
            if (filterStatus) params.isActive = filterStatus === 'active';

            const response = await getAllAnnouncements(params);
            if (response.status === 'success') {
                setAnnouncements(response.data);
            }
        } catch (err) {
            setError('Failed to load announcements');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, [filterCategory, filterStatus]);

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            startDate: '',
            endDate: '',
            imageUrl: '',
            imageFile: null,
            imageType: 'none',
            link: '',
            category: 'other',
            priority: 'medium'
        });
        setPreviewImage(null);
        setEditingAnnouncement(null);
    };

    // Open modal for create/edit
    const openModal = (announcement = null) => {
        if (announcement) {
            setEditingAnnouncement(announcement);
            setFormData({
                title: announcement.title,
                description: announcement.description,
                startDate: announcement.startDate.split('T')[0],
                endDate: announcement.endDate.split('T')[0],
                imageUrl: announcement.imageUrl || '',
                imageFile: null,
                imageType: announcement.imageType || 'none',
                link: announcement.link || '',
                category: announcement.category,
                priority: announcement.priority
            });
            if (announcement.imageType === 'url') {
                setPreviewImage(announcement.imageUrl);
            } else if (announcement.imageType === 'file') {
                setPreviewImage(getAnnouncementImageUrl(announcement));
            }
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        setShowModal(false);
        resetForm();
        setError(null);
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                imageFile: file,
                imageType: 'file',
                imageUrl: ''
            }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle URL input
    const handleUrlChange = (e) => {
        const url = e.target.value;
        setFormData(prev => ({
            ...prev,
            imageUrl: url,
            imageType: url ? 'url' : 'none',
            imageFile: null
        }));
        setPreviewImage(url || null);
    };

    // Clear image
    const clearImage = () => {
        setFormData(prev => ({
            ...prev,
            imageUrl: '',
            imageFile: null,
            imageType: 'none'
        }));
        setPreviewImage(null);
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setError(null);

        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('description', formData.description);
            data.append('startDate', formData.startDate);
            data.append('endDate', formData.endDate);
            data.append('category', formData.category);
            data.append('priority', formData.priority);

            if (formData.link) {
                data.append('link', formData.link);
            }

            if (formData.imageType === 'file' && formData.imageFile) {
                data.append('image', formData.imageFile);
            } else if (formData.imageType === 'url' && formData.imageUrl) {
                data.append('imageUrl', formData.imageUrl);
            } else {
                data.append('imageUrl', '');
            }

            let response;
            if (editingAnnouncement) {
                response = await updateAnnouncement(editingAnnouncement._id, data);
            } else {
                response = await createAnnouncement(data);
            }

            if (response.status === 'success') {
                setSuccess(editingAnnouncement ? 'Announcement updated successfully' : 'Announcement created successfully');
                closeModal();
                fetchAnnouncements();
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            setError(err.message || 'Failed to save announcement');
        } finally {
            setFormLoading(false);
        }
    };

    // Toggle status
    const handleToggleStatus = async (id) => {
        try {
            const response = await toggleAnnouncementStatus(id);
            if (response.status === 'success') {
                fetchAnnouncements();
                setSuccess('Status updated successfully');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            setError('Failed to update status');
        }
    };

    // Delete announcement
    const handleDelete = async (id) => {
        try {
            const response = await deleteAnnouncement(id);
            if (response.status === 'success') {
                fetchAnnouncements();
                setSuccess('Announcement deleted successfully');
                setDeleteConfirm(null);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            setError('Failed to delete announcement');
        }
    };

    // Filter announcements by search
    const filteredAnnouncements = announcements.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Megaphone className="w-7 h-7 text-usiu-blue-600 dark:text-usiu-blue-400" />
                        Event Announcements
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Manage upcoming events and announcements for students and staff
                    </p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="flex items-center gap-2 px-4 py-2.5 bg-usiu-blue-600 hover:bg-usiu-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                    <Plus className="w-5 h-5" />
                    New Announcement
                </button>
            </div>

            {/* Alerts */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mb-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg flex items-center justify-between"
                    >
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                            <span className="text-red-700 dark:text-red-300">{error}</span>
                        </div>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {success && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mb-4 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 rounded-r-lg flex items-center justify-between"
                    >
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-green-700 dark:text-green-300">{success}</span>
                        </div>
                        <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search announcements..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                        />
                    </div>
                    <div className="flex gap-3">
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                        >
                            <option value="">All Categories</option>
                            {CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Announcements List */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-usiu-blue-200 border-t-usiu-blue-600"></div>
                </div>
            ) : filteredAnnouncements.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-800">
                    <Megaphone className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No announcements found</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first announcement to get started</p>
                    <button
                        onClick={() => openModal()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-usiu-blue-600 text-white rounded-lg hover:bg-usiu-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Create Announcement
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAnnouncements.map((announcement) => {
                        const imageUrl = getAnnouncementImageUrl(announcement);
                        const priorityStyle = PRIORITIES.find(p => p.value === announcement.priority);

                        return (
                            <motion.div
                                key={announcement._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`bg-white dark:bg-slate-900 rounded-xl border ${
                                    announcement.isActive
                                        ? 'border-gray-200 dark:border-gray-800'
                                        : 'border-gray-300 dark:border-gray-700 opacity-60'
                                } overflow-hidden shadow-sm hover:shadow-md transition-all`}
                            >
                                {/* Image */}
                                {imageUrl && (
                                    <div className="h-40 overflow-hidden">
                                        <img
                                            src={imageUrl}
                                            alt={announcement.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.parentElement.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}

                                <div className="p-4">
                                    {/* Category & Priority */}
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-usiu-blue-600 dark:text-usiu-blue-400 uppercase tracking-wider">
                                            {announcement.category.replace('-', ' ')}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle?.color}`}>
                                            {priorityStyle?.label}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                                        {announcement.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                                        {announcement.description}
                                    </p>

                                    {/* Dates */}
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span>{formatDate(announcement.startDate)} - {formatDate(announcement.endDate)}</span>
                                    </div>

                                    {/* Status badge */}
                                    <div className="flex items-center justify-between mb-4">
                                        {new Date(announcement.endDate) < new Date() ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs font-medium border border-red-200 dark:border-red-800">
                                                <AlertCircle className="w-3 h-3" />
                                                Expired
                                            </span>
                                        ) : (
                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                                announcement.isActive
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                            }`}>
                                                {announcement.isActive ? (
                                                    <>
                                                        <Eye className="w-3 h-3" />
                                                        Active
                                                    </>
                                                ) : (
                                                    <>
                                                        <EyeOff className="w-3 h-3" />
                                                        Inactive
                                                    </>
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                                        <button
                                            onClick={() => openModal(announcement)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-usiu-blue-600 dark:text-usiu-blue-400 hover:bg-usiu-blue-50 dark:hover:bg-usiu-blue-900/20 rounded-lg transition-colors"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(announcement._id)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                        >
                                            {announcement.isActive ? (
                                                <>
                                                    <EyeOff className="w-4 h-4" />
                                                    Hide
                                                </>
                                            ) : (
                                                <>
                                                    <Eye className="w-4 h-4" />
                                                    Show
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(announcement._id)}
                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                        onClick={(e) => e.target === e.currentTarget && closeModal()}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                                </h2>
                                <button
                                    onClick={closeModal}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                        placeholder="e.g., AIDS Awareness Week"
                                        required
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                        Description *
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        rows={3}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white resize-none"
                                        placeholder="Brief description of the event..."
                                        required
                                    />
                                </div>

                                {/* Dates */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                            Start Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.startDate}
                                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                            End Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.endDate}
                                            onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                            min={formData.startDate}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Category & Priority */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                            Category
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                        >
                                            {CATEGORIES.map(cat => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                            Priority
                                        </label>
                                        <select
                                            value={formData.priority}
                                            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                        >
                                            {PRIORITIES.map(p => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Image */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                        Banner Image (optional)
                                    </label>
                                    <div className="space-y-3">
                                        {/* File upload */}
                                        <div className="flex items-center gap-3">
                                            <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg cursor-pointer transition-colors">
                                                <Upload className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">Upload Image</span>
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                                    onChange={handleFileChange}
                                                    className="hidden"
                                                />
                                            </label>
                                            <span className="text-xs text-gray-500">or</span>
                                            <input
                                                type="url"
                                                value={formData.imageUrl}
                                                onChange={handleUrlChange}
                                                placeholder="Paste image URL"
                                                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                            />
                                        </div>

                                        {/* Preview */}
                                        {previewImage && (
                                            <div className="relative inline-block">
                                                <img
                                                    src={previewImage}
                                                    alt="Preview"
                                                    className="h-32 rounded-lg object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={clearImage}
                                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Link */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                        Event Link (optional)
                                    </label>
                                    <div className="relative">
                                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="url"
                                            value={formData.link}
                                            onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 dark:text-white"
                                            placeholder="https://example.com/event-details"
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-5 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={formLoading}
                                        className="px-5 py-2.5 bg-usiu-blue-600 hover:bg-usiu-blue-700 text-white font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        {formLoading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-4 h-4" />
                                                {editingAnnouncement ? 'Update' : 'Create'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                        onClick={() => setDeleteConfirm(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6"
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Announcement?</h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    This action cannot be undone. The announcement will be permanently removed.
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleDelete(deleteConfirm)}
                                        className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AnnouncementManagement;
